import {
  LIFECYCLE_STAGES,
  LifecycleStageLabel,
} from '@app/components/lifecycle/lifecycle-stages';
import Text from '@shared/components/content/text';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@shared/components/ui/tooltip';
import { cn } from '@shared/lib/utils';

interface LifecycleSplitBarProps {
  farming: number | null;
  processing: number | null;
  packaging: number | null;
  transport: number | null;
  className?: string;
}

export default function LifecycleSplitBar({
  farming,
  processing,
  packaging,
  transport,
  className,
}: LifecycleSplitBarProps) {
  const valuesByStage: Record<LifecycleStageLabel, number> = {
    Farming: farming ?? 0,
    Processing: processing ?? 0,
    Packaging: packaging ?? 0,
    Transport: transport ?? 0,
  };

  const total =
    valuesByStage.Farming +
    valuesByStage.Processing +
    valuesByStage.Packaging +
    valuesByStage.Transport;

  if (total <= 0) {
    return null;
  }

  const percentage = (stage: LifecycleStageLabel) =>
    `${Math.round((valuesByStage[stage] / total) * 100)}%`;

  return (
    <TooltipProvider>
      <div className={cn('space-y-2', className)}>
        <div className="flex h-2 w-full overflow-hidden rounded-full">
          {LIFECYCLE_STAGES.map(({ stage, color }) => (
            <Tooltip key={stage} delayDuration={200}>
              <TooltipTrigger
                asChild
                style={{
                  width: `${(valuesByStage[stage] / total) * 100}%`,
                  backgroundColor: color,
                }}
              >
                <div />
              </TooltipTrigger>
              <TooltipContent className="z-50">
                <p className="text-sm">
                  {stage} {percentage(stage)}
                </p>
              </TooltipContent>
            </Tooltip>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
          {LIFECYCLE_STAGES.map(({ stage, color }) => (
            <div
              key={stage}
              className="flex items-center gap-1 whitespace-nowrap"
            >
              <div
                className="size-1.5 shrink-0 rounded-full"
                style={{ backgroundColor: color }}
              />
              <Text
                variant="subtle"
                className="text-xs"
                includeHover
                hoverText={`${stage} ${percentage(stage)}`}
              >
                {stage}
              </Text>
            </div>
          ))}
        </div>
      </div>
    </TooltipProvider>
  );
}
